/**
 * Content API Adapters
 * 
 * This module provides functions to convert backend API responses to frontend display format
 */

import { ContentItemForDisplay, ContentType, SubType } from './types';

// Backend content item interface (from content service)
interface BackendContentItem {
  id: number;
  title: string;
  summary: string;
  content?: string;
  content_type: string;
  sub_content_type?: string;
  trust_score: number;
  bias_rating: string;
  image_url?: string;
  video_url?: string;
  audio_url?: string;
  asset_path?: string;
  duration?: string;
  topics: string;
  published_at: string;
  url: string;
  source_id: number;
  brand_id?: number;
  author?: string;
  background_style?: string;
  text_color?: string;
  extracted_images?: string[];
  publisher?: {
    name: string;
    domain?: string;
    logo_url?: string;
    description?: string;
    image: string;
    date: string;
  };
  // Logo management fields
  logo_id?: number | null;
  logo_display_mode?: string | null;
  resolved_logo?: {
    id: number;
    brand_id: number;
    logo_key: string;
    display_name: string;
    url: string;
    width?: number;
    height?: number;
    file_size_kb?: number;
    is_default: boolean;
    display_order: number;
  } | null;
  resolved_display_mode?: string | null;
  // Metadata fields (Tier 1, 2, and 3)
  reading_time?: number;
  complexity_level?: string;
  word_count?: number;
  section?: string;
  category?: string;
  key_topics?: string[];
  named_entities?: string[];
  sentiment_overall?: string;
  platform_suitability?: Record<string, boolean>;
  fact_ratio?: number;
  opinion_ratio?: number;
  source_credibility?: number;
  evidence_quality?: string;
  sentiment_score?: number;
  extraction_method?: string;
  fetch_method?: string;
  // Pin functionality
  is_pinned?: boolean;
  pinned_at?: string;
  // Archive functionality
  is_archived?: boolean;
  archived_at?: string;
  // Video platform metadata (for YouTube/Vimeo embeds)
  video_provider?: string;
  video_id?: string;
  // Consolidated media fields
  media_urls?: string[];
  primary_media_url?: string;
}

// Helper functions to map string values to enums
const mapStringToContentType = (contentType: string): ContentType => {
  switch (contentType?.toLowerCase()) {
    case 'text':
      return ContentType.TEXT;
    case 'audio':
    case 'podcast':
      return ContentType.AUDIO;
    case 'video':
      return ContentType.VIDEO;
    case 'image':
      return ContentType.IMAGE;
    default:
      return ContentType.TEXT;
  }
};

const mapStringToSubType = (subContentType: string): SubType | undefined => {
  switch (subContentType?.toLowerCase()) {
    case 'article':
      return SubType.ARTICLE;
    case 'newsletter':
      return SubType.NEWSLETTER;
    case 'spark':
      return SubType.SPARK;
    case 'podcast':
      return SubType.PODCAST;
    case 'song':
      return SubType.SONG;
    case 'memo':
      return SubType.MEMO;
    case 'long':
      return SubType.LONG;
    case 'short':
      return SubType.SHORT;
    case 'live':
      return SubType.LIVE;
    case 'single':
      return SubType.SINGLE;
    case 'gallery':
      return SubType.GALLERY;
    default:
      return undefined;
  }
};

const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

/**
 * Convert backend content item to frontend display format
 */
export const contentAdapter = {
  toDisplayFormat: (item: BackendContentItem): ContentItemForDisplay => {
    // Generate engagement data (placeholder for now)
    const engagement = {
      likes: Math.floor(Math.random() * 1000),
      comments: Math.floor(Math.random() * 300),
      shares: Math.floor(Math.random() * 200)
    };

    // Extract publisher information
    let publisherInfo;
    if (item.publisher) {
      publisherInfo = {
        name: item.publisher.name,
        image: item.publisher.logo_url || item.publisher.image,
        date: item.publisher.date,
        domain: item.publisher.domain,
        logo_url: item.publisher.logo_url,
        description: item.publisher.description
      };
    } else {
      // Fallback to author-based publisher info
      publisherInfo = {
        name: item.author || 'Unknown Publisher',
        image: `https://randomuser.me/api/portraits/men/${Math.abs(item.source_id) % 99 + 1}.jpg`,
        date: formatDate(item.published_at)
      };
    }

    // Prefer RSS hero image (image_url) over extracted images
    // RSS image_url comes from media:content tag - the publisher's chosen hero image
    // extracted_images[0] is often an author headshot or sidebar image from HTML parsing
    const displayImage = item.image_url
      || (item.extracted_images && item.extracted_images.length > 0 ? item.extracted_images[0] : null);

    // Map logo fields from API naming to ContentCard naming
    const logoMapping: any = {
      id: item.id,
      title: item.title,
      url: item.url,
      summary: item.summary,
      content: item.content,
      content_type: mapStringToContentType(item.content_type),
      sub_content_type: mapStringToSubType(item.sub_content_type || ''),
      trust_score: item.trust_score || 75,
      bias_rating: item.bias_rating || 'Neutral',
      publisher: publisherInfo,
      topics: item.topics ? item.topics.split(',').map(t => t.trim()).filter(t => t) : [],
      image_url: displayImage,
      video_url: item.video_url,
      audio_url: item.audio_url,
      asset_path: item.asset_path,
      extracted_images: item.extracted_images || [],
      duration: item.duration,
      background_style: item.background_style,
      text_color: item.text_color,
      engagement,
      // Metadata fields
      author: item.author,
      reading_time: item.reading_time,
      complexity_level: item.complexity_level,
      word_count: item.word_count,
      published_at: item.published_at,
      section: item.section,
      category: item.category,
      key_topics: item.key_topics,
      named_entities: item.named_entities,
      sentiment_overall: item.sentiment_overall,
      platform_suitability: item.platform_suitability,
      fact_ratio: item.fact_ratio,
      opinion_ratio: item.opinion_ratio,
      source_credibility: item.source_credibility,
      evidence_quality: item.evidence_quality,
      sentiment_score: item.sentiment_score,
      extraction_method: item.extraction_method,
      fetch_method: item.fetch_method,
      // Logo mapping: API fields → ContentCard props
      brandId: item.brand_id,
      brandName: item.publisher?.name,
      contentItemLogoId: item.logo_id,
      contentItemLogoDisplayMode: item.logo_display_mode as any,
      brandDefaultLogo: item.resolved_logo ? {
        id: item.resolved_logo.id,
        logoKey: item.resolved_logo.logo_key,
        displayName: item.resolved_logo.display_name,
        url: item.resolved_logo.url,
        width: item.resolved_logo.width,
        height: item.resolved_logo.height,
        fileSizeKb: item.resolved_logo.file_size_kb,
        isDefault: item.resolved_logo.is_default,
        displayOrder: item.resolved_logo.display_order
      } : null,
      brandLogoDisplayMode: (item.resolved_display_mode || 'icon_and_name') as any,
      // Pin functionality
      is_pinned: item.is_pinned || false,
      pinned_at: item.pinned_at,
      // Archive functionality
      is_archived: item.is_archived || false,
      archived_at: item.archived_at,
      // Video platform metadata (for YouTube/Vimeo embeds)
      video_provider: item.video_provider,
      video_id: item.video_id,
      // Consolidated media fields
      media_urls: item.media_urls,
      primary_media_url: item.primary_media_url
    };

    return logoMapping;
  }
};